import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useSearch } from '@client/hooks/use-search.hook';
import { NodonTheme } from '@client/style';
import { SelectListChildren } from '@ui/menus/menu.model';
import VirtualizedItems from './VirtualizedItems';
import { Box, List, ListProps, TextFieldProps } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { RowComponentProps } from 'react-window';
import { makeStyles } from 'tss-react/mui';

import EmptyState, { EmptyStateProps } from './EmptyState';
import SearchField from './SearchField';

export interface SelectListProps<T> {
  items?: T[];
  enableSearch?: boolean;
  enableVirtualization?: boolean;
  width?: number | 'auto';

  /** Filter items based on this string */
  searchString?: string;

  /** If there is no items, show a message */
  emptyStateProps?: EmptyStateProps;
  listProps?: ListProps;

  onSearch?: (searchString: string) => void;
  children: SelectListChildren<T> | React.ReactNode;
}

const SelectList = <T,>({
  children,
  items,
  enableSearch = false,
  enableVirtualization = false,
  width = 'auto',
  searchString,
  emptyStateProps,
  listProps: externalListProps,
  onSearch,
}: SelectListProps<T>) => {
  const { classes } = useStyles();

  const [localSearchString, setLocalSearchString] = useState(searchString);
  const filteredItems = useSearch(items, localSearchString);

  const searchTextFieldProps = useObjectMemo<TextFieldProps>({
    variant: 'outlined',
    slotProps: {
      input: {
        classes: { notchedOutline: classes.notchedOutline },
      },
      htmlInput: {
        sx: { padding: '10px 14px' },
      },
    },
    sx: {
      borderBottom: '1px solid',
      borderColor:
        filteredItems.length > 0
          ? NodonTheme.palette.neutral.lightest
          : 'transparent',
    },
  });

  const listProps = useObjectMemo<ListProps>({
    ...externalListProps,
    sx: {
      width,
      overflow: 'auto',
      ...externalListProps?.sx,
    },
  });

  const handleSearch = useCallback(
    (searchString: string) => {
      if (onSearch) {
        onSearch(searchString);
      }
    },
    [onSearch],
  );

  /* 
    Note: use an effect to update search value if the searching is handled outside this component 
  */
  useEffect(() => {
    if (searchString !== localSearchString) {
      setLocalSearchString(searchString);
    }
  }, [searchString, localSearchString]);

  return (
    <>
      {enableSearch && (
        <SearchField
          {...searchTextFieldProps}
          autoFocus
          fullWidth
          value={localSearchString}
          onChange={handleSearch}
        />
      )}

      {emptyStateProps && filteredItems.length <= 0 && (
        <EmptyState {...emptyStateProps} />
      )}

      {typeof children === 'function' ? (
        <FilteredItems
          {...listProps}
          items={filteredItems}
          enableVirtualization={enableVirtualization}
        >
          {children}
        </FilteredItems>
      ) : (
        <List {...listProps}>{children}</List>
      )}
    </>
  );
};

interface FilteredItemsProps<T> extends Omit<ListProps, 'children'> {
  items: T[];
  enableVirtualization: boolean;
  children: SelectListChildren<T>;
}

const FilteredItems = <T,>({
  items,
  enableVirtualization,
  children,
  ...listProps
}: FilteredItemsProps<T>) => {
  const renderChildren = useCallback(
    ({ index, style }: RowComponentProps) => {
      const item = items[index];
      return item ? children(item, style) : null;
    },
    [children, items],
  );

  return enableVirtualization ? (
    <Box
      sx={{
        flex: 1,
      }}
    >
      <VirtualizedItems items={items}>{renderChildren}</VirtualizedItems>
    </Box>
  ) : (
    <List {...listProps}>
      {items.map((item, index) => children(item, undefined, index))}
    </List>
  );
};

const useStyles = makeStyles()(() => ({
  notchedOutline: {
    border: 0,
  },
}));

export default SelectList;
